import type { LoadingConditions } from '@/types/sell';
import type { GoodsPropertiesSet } from '@/types/buy';

// Встречная закупка покупателя под мой оффер на продажу: просмотр + создание сделки «Продать».
// GET /order/purchase/by_id (bargainType=AGENT_CPT) + POST /order/bargain/create/purchase_order/agent_cpt.
// Для продавца главная цена — FCA у ворот (fcaPricePerTonWithoutNds, == best-price виджета), CPT —
// что платит покупатель с доставкой. Создание сделки требует подписанного «поручения агенту».

export interface PurchaseOrderFile {
  readonly id: number;
  readonly name?: string;
}

/** Свойство приёмки покупателя (диктует покупатель; для продавца — read-only условие). */
export interface PurchaseQaProperty {
  readonly name: string;
  readonly gostValue?: string;
  /** Приёмка «не по ГОСТу»: порог, %, выше которого снижается вес. */
  readonly increaseValue?: number;
  /** Приёмка «не по ГОСТу»: снижение веса, %. */
  readonly decreaseWeightValue?: number;
}

export interface PurchaseOrderInfo {
  readonly id: number;
  readonly version?: number;
  readonly userInfo?: { readonly name?: string; readonly inn?: string };
  readonly goodsType?: string;
  readonly cropYear?: number;
  readonly address?: string;
  readonly addressTitle?: string;
  readonly weightTon?: number;
  readonly userComment?: string;
  readonly qualityCreditType?: string; // BY_AVERAGE_VALUE | BY_EACH_DAY
  readonly qualityAcceptanceByGost?: boolean;
  readonly qualityAcceptanceProperties?: PurchaseQaProperty[];
  readonly specialTermsFiles?: PurchaseOrderFile[];
  // Качество культуры (протеин и т.п.) для сводки сделки — первое свойство показываем чипом.
  readonly goodsPropertiesSet?: Pick<GoodsPropertiesSet, 'properties'>;
}

/** Цена встречной закупки по варианту предоплаты. Цены стороны покупателя (CPT) продавцу сервер не отдаёт. */
export interface PurchasePriceInfo {
  readonly fcaPricePerTonWithoutNds?: number; // что получает продавец, ₽/т без НДС
  readonly fullPriceFCAWithoutNds?: number; // итог продавца
  readonly prepaymentPercent?: number;
}

export interface PurchaseOrderView {
  readonly order: PurchaseOrderInfo;
  readonly fromAddress?: { readonly address?: string; readonly title?: string };
  readonly toAddress?: { readonly address?: string; readonly title?: string };
  readonly distance?: number;
  readonly weightTon?: number;
  /** Ключ — процент предоплаты ('0' | '100'); значение — цены этого варианта. */
  readonly priceByOptions?: Record<string, PurchasePriceInfo>;
  readonly agentAssignmentFile?: PurchaseOrderFile;
  // Условия ПРОДАВЦА (мои), подтянутые бэкендом из связанного sale-ордера на верхний уровень ответа
  // (== старый портал pageData.loadingConditions/…). Показываем read-only в «Ваши условия».
  readonly loadingConditions?: LoadingConditions;
  readonly declarationNo?: string;
  readonly declarationDate?: string;
  readonly declarationFiles?: PurchaseOrderFile[];
}

export type SellPrepaymentType = 'PERCENT_0' | 'PERCENT_100';

/** Процент предоплаты → тип (ключ priceByOptions → enum бэкенда). */
export function prepaymentTypeFromPercent(percent: string | number): SellPrepaymentType {
  return String(percent) === '100' ? 'PERCENT_100' : 'PERCENT_0';
}

/** Тип предоплаты → процент-ключ priceByOptions. */
export function percentKeyFromPrepayment(type: SellPrepaymentType): string {
  return type === 'PERCENT_100' ? '100' : '0';
}

// Общие поля сделки продавца — для генерации поручения и создания. bargainType сервис добавляет сам.
// Две ветки (взаимоисключающие):
//  • С заявкой (fromSaleOrderId): бэкенд резолвит логистику/адрес из заявки — partnerAddress/loadingConditions
//    слать НЕЛЬЗЯ (400 «not supported»).
//  • Без заявки (из свободного поиска): обязательны weightTon + loadingConditions + partnerAddress/Title (мой базис).
export interface SellDealParams {
  readonly orderId: number; // встречная закупка покупателя
  readonly orderVersion: number;
  readonly creationSection: 'SELL_BEST_PRICE';
  readonly isBestPrice: boolean;
  readonly userComment: string;
  readonly prepaymentType: SellPrepaymentType;
  /** Мой оффер на продажу — ветка «с заявкой» (из него резолвится точка погрузки/логистика). */
  readonly fromSaleOrderId?: number;
  /** Ветка «без заявки»: объём, логистика, адрес погрузки и декларация заполняет продавец. */
  readonly weightTon?: number;
  readonly loadingConditions?: LoadingConditions;
  readonly partnerAddress?: string;
  readonly partnerAddressTitle?: string;
  /** Декларация (без заявки обязательна; с заявкой резолвится из неё). Дата — «dd.MM.yyyy». */
  readonly declarationNo?: string;
  readonly declarationDate?: string;
  readonly declarationFileIds?: number[];
}

/** Тело создания сделки: deal-параметры + подписанное поручение агенту. */
export interface CreateDealFromPurchaseRequest extends SellDealParams {
  readonly agentAssignmentFileId: number;
}
